import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import MarketingNav from "@/components/layout/MarketingNav";
import Footer from "@/components/Footer";
import { PLAY_STORE_URL } from "@/data/aboutContent";
import { supabase } from "@/lib/supabase";

type StructureItem = { title: string; body: string };

type PageContent = {
  eyebrow: string;
  title: string;
  tagline: string;
  what_title: string;
  what_body: string;
  who_title: string;
  who_body: string;
  structure_title: string;
  structure_body: string;
  structure_items: StructureItem[];
  faces_title: string;
  faces_subtitle: string;
  cta_label: string;
  cta_url: string;
  hero_image_url: string;
};

type Face = {
  id: string;
  name: string;
  school: string;
  quote: string;
  photo_url: string;
};

const FALLBACK: PageContent = {
  eyebrow: "SCHOLARS PROGRAM",
  title: "Learn. Discover. Build the evidence.",
  tagline:
    "Skutopia helps African secondary students learn, discover opportunities, and build the skills and evidence to pursue them.",
  what_title: "What the Scholars Program is",
  what_body:
    "A guided path inside the Skutopia app that helps students understand who they are, find real opportunities, and prepare competitive applications — week by week.",
  who_title: "Who it is for",
  who_body:
    "African secondary students who want university and scholarship pathways — and the partners and mentors who support them.",
  structure_title: "How the program is structured",
  structure_body:
    "Twenty-four weeks of missions. Students unlock work as they go, save answers as evidence, and move from identity to opportunity literacy to application readiness.",
  structure_items: [
    {
      title: "Identity & story",
      body: "Map interests, skills, values, and the moments that prove them.",
    },
    {
      title: "Opportunity world",
      body: "Learn what counts, spot scams, research programmes, and shortlist real options.",
    },
    {
      title: "Evidence & applications",
      body: "Turn experience into claims a stranger can check — then plan the next action.",
    },
  ],
  faces_title: "Faces from the program",
  faces_subtitle:
    "Students who have walked the Scholars path — their schools, and words they stand by.",
  cta_label: "Get the app",
  cta_url: "",
  hero_image_url: "",
};

function asStructureItems(raw: unknown): StructureItem[] {
  if (!Array.isArray(raw)) return FALLBACK.structure_items;
  const items = raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    .map((item) => ({
      title: String(item.title ?? "").trim(),
      body: String(item.body ?? "").trim(),
    }))
    .filter((item) => item.title || item.body);
  return items.length ? items : FALLBACK.structure_items;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Scholars = () => {
  const [page, setPage] = useState<PageContent>(FALLBACK);
  const [faces, setFaces] = useState<Face[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [pageRes, facesRes] = await Promise.all([
        supabase
          .from("marketing_scholars_page")
          .select(
            "eyebrow, title, tagline, what_title, what_body, who_title, who_body, structure_title, structure_body, structure_items, faces_title, faces_subtitle, cta_label, cta_url, hero_image_url",
          )
          .eq("id", 1)
          .maybeSingle(),
        supabase
          .from("marketing_scholars_faces")
          .select("id, name, school, quote, photo_url")
          .eq("is_visible", true)
          .order("sort_order", { ascending: true }),
      ]);
      if (!active) return;
      if (pageRes.data) {
        setPage({
          ...FALLBACK,
          ...pageRes.data,
          structure_items: asStructureItems(pageRes.data.structure_items),
        });
      }
      if (facesRes.data) setFaces(facesRes.data as Face[]);
    })();
    return () => {
      active = false;
    };
  }, []);

  const ctaHref = page.cta_url?.trim() || PLAY_STORE_URL;
  const heroPhoto = page.hero_image_url?.trim();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MarketingNav />
      <main>
        {/* Hero — brand + one mission line + CTA + face-grid atmosphere */}
        <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-skutopia-950 text-white">
          {heroPhoto ? (
            <img
              src={heroPhoto}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-px opacity-40">
              {(faces.length ? faces : Array.from({ length: 24 }, (_, i) => ({
                id: `placeholder-${i}`,
                name: "",
                school: "",
                quote: "",
                photo_url: "",
              }))).slice(0, 32).map((face, index) => (
                <div
                  key={face.id || index}
                  className="aspect-square bg-skutopia-800/80 overflow-hidden"
                >
                  {face.photo_url ? (
                    <img src={face.photo_url} alt="" className="h-full w-full object-cover grayscale" />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-skutopia-700 to-skutopia-900" />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-skutopia-950 via-skutopia-950/75 to-skutopia-900/35" />
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32">
            <motion.div initial="hidden" animate="visible" className="max-w-3xl">
              <motion.p
                variants={fadeUp}
                custom={0}
                className="text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-skutopia-200 mb-5"
              >
                {page.eyebrow}
              </motion.p>
              <motion.h1
                variants={fadeUp}
                custom={1}
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
              >
                {page.title}
              </motion.h1>
              <motion.p
                variants={fadeUp}
                custom={2}
                className="text-lg sm:text-xl text-white/85 leading-relaxed mb-10 max-w-2xl"
              >
                {page.tagline}
              </motion.p>
              <motion.div variants={fadeUp} custom={3}>
                <a
                  href={ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-white px-8 py-3.5 text-base font-semibold text-skutopia-900 hover:bg-skutopia-50 transition-colors"
                >
                  {page.cta_label}
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* What / Who */}
        <section className="py-20 sm:py-28 bg-[#f7f8f6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-skutopia-600 mb-4">
                What we do
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-skutopia-950 tracking-tight mb-5">
                {page.what_title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                {page.what_body}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-skutopia-600 mb-4">
                Who we serve
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-skutopia-950 tracking-tight mb-5">
                {page.who_title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                {page.who_body}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Structure */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-14">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-skutopia-600 mb-4">
                Structure
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-skutopia-950 tracking-tight mb-5">
                {page.structure_title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                {page.structure_body}
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              {page.structure_items.map((item, index) => (
                <motion.div
                  key={`${item.title}-${index}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="border-t-2 border-skutopia-600 pt-6"
                >
                  <p className="text-sm font-semibold text-skutopia-600 mb-2">
                    {String(index + 1).padStart(2, "0")}
                  </p>
                  <h3 className="text-xl font-bold text-skutopia-950 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Faces */}
        <section className="py-20 sm:py-28 bg-skutopia-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-skutopia-300 mb-4">
                Alumni voices
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                {page.faces_title}
              </h2>
              <p className="text-lg text-white/75 leading-relaxed">{page.faces_subtitle}</p>
            </div>
            {faces.length === 0 ? (
              <p className="text-white/60 text-sm">Participant stories will appear here soon.</p>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {faces.map((face, index) => (
                  <motion.article
                    key={face.id}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(index * 0.05, 0.3) }}
                    className="group overflow-hidden rounded-none bg-skutopia-900/60"
                  >
                    <div className="aspect-[4/5] bg-skutopia-800 overflow-hidden">
                      {face.photo_url ? (
                        <img
                          src={face.photo_url}
                          alt={face.name}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-skutopia-700 to-skutopia-900 text-4xl font-bold text-white/70">
                          {face.name
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((part) => part[0] ?? "")
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-semibold">{face.name}</h3>
                      {face.school ? (
                        <p className="mt-1 text-sm text-skutopia-200">{face.school}</p>
                      ) : null}
                      {face.quote ? (
                        <p className="mt-4 text-sm leading-relaxed text-white/80">
                          “{face.quote}”
                        </p>
                      ) : null}
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-xl sm:text-2xl font-semibold text-skutopia-950 max-w-xl">
              Start the Scholars path in the Skutopia app.
            </p>
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-skutopia-700 px-7 py-3 text-sm font-semibold text-white hover:bg-skutopia-800 transition-colors"
            >
              {page.cta_label}
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Scholars;
